import { useEffect } from 'react';
import { RouterProvider } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { router } from './routes';
import { QueryProvider } from './providers';
import { initOfflineDetection } from './stores';
import {
  InstallPrompt,
  OfflineIndicator,
  PWAUpdatePrompt,
} from './components/pwa';
import { ToastProvider } from './components/ui/ToastProvider';
import { UiStoreToastBridge } from './components/ui/UiStoreToastBridge';
import { ErrorBoundary } from './components/common/ErrorBoundary';
import { authService } from './services/authService';
import { peekDeviceId, setDeviceId } from './utils/deviceId';
import { useAuthStore } from './stores/authStore';

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined;

/**
 * Root App component
 *
 * Provides all necessary context providers for the application:
 * - QueryProvider: TanStack Query for server state management
 * - RouterProvider: React Router for navigation
 *
 * Also initializes global event listeners (offline detection)
 * and includes PWA update prompts.
 */
export function App(): React.ReactElement {
  // Initialize offline detection on mount
  useEffect(() => {
    const cleanup = initOfflineDetection();
    return cleanup;
  }, []);

  // Resync the local device ID from the sb_device HttpOnly cookie on app boot.
  // After iOS Safari ITP wipes localStorage, this is how the merchant PWA recovers
  // its canonical device identity so subsequent /auth/login + /auth/pin/verify calls
  // hit the existing trusted_devices row (no duplicate row is created).
  // Fire-and-forget — failures (no cookie / 404) are expected on first-time visits.
  useEffect(() => {
    // Perf gate: if both deviceId and lastKnownUserName are intact, cookie and localStorage
    // already agree — skip the network round trip (matters on 3G).
    const hasLocalDeviceId = peekDeviceId() !== null;
    const hasKnownUser = useAuthStore.getState().lastKnownUserName !== null;
    if (hasLocalDeviceId && hasKnownUser) {
      return;
    }

    let cancelled = false;
    authService.resolveMyDevice()
      .then((device) => {
        if (!cancelled && device) {
          setDeviceId(device.deviceId);
        }
      })
      .catch(() => {
        // Network / parse errors are silent — we'll retry on next boot.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const content = (
    <ErrorBoundary>
      <QueryProvider>
        <ToastProvider>
          <UiStoreToastBridge />
          <OfflineIndicator />
          <RouterProvider router={router} />
          <InstallPrompt />
          <PWAUpdatePrompt />
        </ToastProvider>
      </QueryProvider>
    </ErrorBoundary>
  );

  // Wrap with Google OAuth provider if client ID is configured
  if (GOOGLE_CLIENT_ID) {
    return (
      <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
        {content}
      </GoogleOAuthProvider>
    );
  }

  return content;
}
